/** Dialog — Radix-powered modal with NVL backdrop blur + scale animation. */
import { forwardRef } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "../../lib/cn";

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogPortal = DialogPrimitive.Portal;
export const DialogClose = DialogPrimitive.Close;

export const DialogOverlay = forwardRef(function DialogOverlay({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Overlay
      ref={ref}
      className={cn(
        "fixed inset-0 z-[200] bg-[rgba(8,5,10,0.55)] backdrop-blur-xl",
        "data-[state=open]:animate-fade-in data-[state=closed]:opacity-0 transition-opacity",
        className
      )}
      {...props}
    />
  );
});

export const DialogContent = forwardRef(function DialogContent(
  { className, children, hideClose = false, ...props },
  ref
) {
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          "fixed left-1/2 top-1/2 z-[210] -translate-x-1/2 -translate-y-1/2 w-[92vw] max-w-[520px]",
          "bg-bg-elev border border-line-2 shadow-float rounded-[22px] p-7",
          "data-[state=open]:animate-reveal-up focus-visible:outline-none",
          className
        )}
        {...props}
      >
        {children}
        {!hideClose && (
          <DialogClose
            className="absolute right-4 top-4 w-9 h-9 rounded-full inline-flex items-center justify-center text-text-dim hover:text-text hover:bg-surface-2 transition-colors"
            aria-label="Close"
          >
            <X size={16} />
          </DialogClose>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});

export function DialogHeader({ className, ...props }) {
  return <div className={cn("flex flex-col gap-1.5 mb-5", className)} {...props} />;
}

export const DialogTitle = forwardRef(function DialogTitle({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Title
      ref={ref}
      className={cn("font-display text-[24px] tracking-[-0.01em] leading-tight", className)}
      {...props}
    />
  );
});

export const DialogDescription = forwardRef(function DialogDescription({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Description
      ref={ref}
      className={cn("text-[14px] text-text-dim leading-relaxed", className)}
      {...props}
    />
  );
});

export function DialogFooter({ className, ...props }) {
  return (
    <div className={cn("flex flex-wrap items-center justify-end gap-2 mt-6", className)} {...props} />
  );
}
